// Short links (#734) in a message: the menu on a right-click, and the link a long press on a phone starts from. The rule is
// `shared/src/shortUrl.ts`; this file is what the web does with it. A tap or click always opens the full link.
//
// "Show full URL" replaces the short form with the full link, for that link in that message. It is view state, kept by the link's
// component, not stored: a reload draws the short form again. Once shown full, the same item reads "Show short URL".
import { createContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Item } from "./MessageMenu.tsx";

/** One shortened link, as the menu needs it. `full`: the full link is showing now. */
export interface LinkTarget {
  href: string;
  full: boolean;
  toggle: () => void;
}

export interface ShortLinkHost {
  /** Right-click (desktop): the link menu at the pointer. */
  menu: (t: LinkTarget, at: { x: number; y: number }) => void;
}

/** Set by the message list. Without one (a link outside the list), the browser's own menu stays. */
export const ShortLinkContext = createContext<ShortLinkHost | null>(null);

// A finger on a short link. The row's long press opens the message sheet 0.4 s later, and the sheet reads this to add the
// link's rows. The row clears it at each touch start (capture), before a link sets it, so a hold elsewhere never sees it.
let touched: LinkTarget | null = null;
export const touchLink = (t: LinkTarget | null) => {
  touched = t;
};
export function takeTouchedLink(): LinkTarget | null {
  const t = touched;
  touched = null;
  return t;
}

export const showLabel = (t: Pick<LinkTarget, "full">) => (t.full ? "Show short URL" : "Show full URL");

export const openLink = (href: string) => void window.open(href, "_blank", "noopener,noreferrer");

const W = 220;
const EDGE = 8;

/** The desktop menu: Open link, Copy link, Show full URL / Show short URL. Opens at the pointer; arrows move; Escape closes. */
export function LinkMenu({ target, at, onCopy, onClose }: { target: LinkTarget; at: { x: number; y: number }; onCopy: (href: string) => void; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<React.CSSProperties>({ position: "fixed", top: at.y, left: at.x, opacity: 0 });

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const h = el.offsetHeight;
    const left = at.x + W <= window.innerWidth - EDGE ? at.x : Math.max(EDGE, at.x - W);
    const top = at.y + h <= window.innerHeight - EDGE ? at.y : Math.max(EDGE, at.y - h);
    setPos({ position: "fixed", top, left });
    el.focus({ preventScroll: true });
  }, [at.x, at.y]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && onClose();
    const t = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    window.addEventListener("resize", onClose);
    window.addEventListener("blur", onClose);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("blur", onClose);
    };
  }, []);

  const onKey = (e: React.KeyboardEvent) => {
    const items = [...(box.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    const go = (n: number) => items[(n + items.length) % items.length]?.focus();
    if (e.key === "Escape" || e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      go(i + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      go(i < 0 ? items.length - 1 : i - 1);
    }
  };

  const done = (f: () => void) => () => {
    f();
    onClose();
  };
  return createPortal(
    <div className="msg-more link-menu" ref={box} style={{ ...pos, width: W }} role="menu" aria-label="Link actions" tabIndex={-1} onKeyDown={onKey}>
      <div className="mm-group" role="group">
        <Item icon="open" label="Open link" onClick={done(() => openLink(target.href))} />
        <Item icon="copy" label="Copy link" onClick={done(() => onCopy(target.href))} />
        <Item icon="expand" label={showLabel(target)} onClick={done(target.toggle)} />
      </div>
    </div>,
    document.body,
  );
}
